import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatAUD, formatDateAU } from '../../../shared/format';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';
import type { FeedReconciliationResponse, FeedConflictItem, FeedSyncRunRow } from '../../../shared/api';

function holdingDelta(item: FeedConflictItem): { security: string; cur?: { units: number; costBaseCents: number }; feed?: { units: number; costBaseCents: number } } {
  const security = String((item.payload as { securityCode?: string }).securityCode ?? '—');
  const d = item.conflictDetail as { current?: { units: number; costBaseCents: number }; feed?: { units: number; costBaseCents: number } } | null;
  return { security, cur: d?.current, feed: d?.feed };
}

export function FeedReconciliation() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['feed-reconciliation'],
    queryFn: () => api<{ data: FeedReconciliationResponse }>('/api/admin/feed-reconciliation?state=pending'),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['feed-reconciliation'] });

  const sync = useMutation({
    mutationFn: () => api('/api/admin/feed-reconciliation/sync', { method: 'POST' }),
    onSuccess: refresh,
  });
  const accept = useMutation({
    mutationFn: (id: string) => api(`/api/admin/feed-reconciliation/${id}/accept`, { method: 'POST' }),
    onSuccess: refresh,
  });
  const reject = useMutation({
    mutationFn: (id: string) => api(`/api/admin/feed-reconciliation/${id}/reject`, { method: 'POST' }),
    onSuccess: refresh,
  });
  const busy = sync.isPending || accept.isPending || reject.isPending;

  if (isLoading) return <QASkeleton variant="cards" />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load feed reconciliation</div>;

  const { conflicts, runs } = data.data;

  return (
    <div className="py-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="qa-h2">Feed values that need a <em className="text-rust">decision.</em></h1>
          <p className="text-ink-mute text-sm mt-2 max-w-2xl">
            The position feed (Macquarie wrap, IRESS/CHESS) is authoritative and auto-applies clean
            updates. When a feed holding differs from a value already on file, it waits here:{' '}
            <strong>Accept</strong> to take the feed value, or <strong>Reject</strong> to keep the
            current one.
          </p>
        </div>
        <button type="button" className="qa-btn qa-btn--primary whitespace-nowrap" disabled={busy} onClick={() => sync.mutate()}>
          {sync.isPending ? 'Syncing…' : 'Sync now'}
        </button>
      </header>

      {conflicts.length === 0 ? (
        <QAEmpty inline title="No conflicts to review.">Every feed update reconciled cleanly.</QAEmpty>
      ) : (
        <ul className="space-y-3">
          {conflicts.map((c) => {
            const d = holdingDelta(c);
            return (
              <li key={c.id} className="qa-card p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">
                    {c.clientName} · <span className="text-ink-mute">{c.accountName}</span> · <strong>{d.security}</strong>
                  </div>
                  <div className="text-xs text-ink-mute mt-1 font-mono">
                    on file: {d.cur ? `${d.cur.units} units @ ${formatAUD(d.cur.costBaseCents / 100)}` : '—'}
                    {'  →  '}
                    feed: {d.feed ? `${d.feed.units} units @ ${formatAUD(d.feed.costBaseCents / 100)}` : '—'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" className="qa-btn qa-btn--primary" disabled={busy} onClick={() => accept.mutate(c.id)}>Accept feed</button>
                  <button type="button" className="qa-btn qa-btn--ghost" disabled={busy} onClick={() => reject.mutate(c.id)}>Keep current</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <h2 className="qa-h3 mt-8 mb-3">Recent syncs</h2>
      {runs.length === 0 ? (
        <p className="text-ink-mute text-sm">No syncs yet.</p>
      ) : (
        <ul className="text-sm divide-y divide-hairline">
          {runs.map((r: FeedSyncRunRow) => (
            <li key={r.id} className="py-2 flex items-center justify-between gap-4">
              <span>{r.accountName ?? 'All accounts'} · <span className="text-ink-mute">{r.provider}</span></span>
              <span className="font-mono text-xs text-ink-mute">
                {r.status}
                {r.summary ? ` · ${r.summary.applied ?? 0} applied · ${r.summary.conflicts ?? 0} conflicts` : ''}
                {' · '}{formatDateAU(r.startedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
